import { extensionFor, pickMimeType } from './mime'

export const PREROLL_SECONDS = 1.5
export const TAIL_SECONDS = 0.5

/** Захват микрофона без обработки — это запись голоса, а не звонок. */
export async function requestMicStream(deviceId?: string): Promise<MediaStream> {
  const audio: MediaTrackConstraints = {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
    sampleRate: 48000,
  }
  if (deviceId) audio.deviceId = { exact: deviceId }
  return navigator.mediaDevices.getUserMedia({ audio, video: false })
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return
  for (const track of stream.getTracks()) track.stop()
}

export type MicDevice = { deviceId: string; label: string }

export async function listMicDevices(): Promise<MicDevice[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return []
  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices
    .filter((d) => d.kind === 'audioinput')
    .map((d, index) => ({
      deviceId: d.deviceId,
      label: d.label || `Микрофон ${index + 1}`,
    }))
}

export type RecordingPhase = 'idle' | 'seeking' | 'preroll' | 'recording' | 'finishing'

export type RecordingProgress = {
  phase: RecordingPhase
  /** Сколько секунд осталось до начала реплики (в фазе preroll). */
  countdown: number
  /** Текущий таймкод видео. */
  videoTime: number
}

export type RecordedTake = {
  blob: Blob
  mimeType: string
  filename: string
  /** Длительность записи, сек. */
  duration: number
  /**
   * Поправка синхронизации для сервера: такт ставится на `line.start + offset`.
   * Файл начинается с преролла, поэтому offset обычно отрицательный.
   */
  offset: number
  /** Таймкод видео в момент реального старта рекордера. */
  startedAtVideoTime: number
}

export type RecordOptions = {
  video: HTMLVideoElement
  stream: MediaStream
  lineStart: number
  lineEnd: number
  /** Задержка старта рекордера из калибровки, сек. */
  recorderLag?: number
  preroll?: number
  tail?: number
  onProgress?: (progress: RecordingProgress) => void
}

export class RecordingCancelled extends Error {
  constructor() {
    super('Запись отменена')
    this.name = 'RecordingCancelled'
  }
}

function waitForSeek(video: HTMLVideoElement, target: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - target) < 0.02) {
      resolve()
      return
    }
    const done = (): void => {
      video.removeEventListener('seeked', done)
      resolve()
    }
    video.addEventListener('seeked', done)
    video.currentTime = target
    // Подстраховка: некоторые браузеры не шлют seeked, если позиция не изменилась.
    window.setTimeout(done, 2000)
  })
}

async function decodeDuration(blob: Blob): Promise<number | null> {
  let ctx: AudioContext | null = null
  try {
    ctx = new AudioContext()
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer())
    return decoded.duration
  } catch {
    return null
  } finally {
    if (ctx) await ctx.close().catch(() => undefined)
  }
}

/**
 * Одна сессия записи реплики (docs/RECORDING.md, «Пофразная запись»).
 *
 * 1. отматываем видео на `start - preroll`;
 * 2. одновременно стартуем воспроизведение и MediaRecorder;
 * 3. запоминаем ФАКТИЧЕСКИЙ таймкод видео в момент старта рекордера;
 * 4. останавливаем на `end + tail`.
 */
export class LineRecording {
  private readonly options: Required<Omit<RecordOptions, 'onProgress'>> &
    Pick<RecordOptions, 'onProgress'>
  private recorder: MediaRecorder | null = null
  private rafId: number | null = null
  private cancelled = false
  private finished = false

  constructor(options: RecordOptions) {
    this.options = {
      video: options.video,
      stream: options.stream,
      lineStart: options.lineStart,
      lineEnd: options.lineEnd,
      recorderLag: options.recorderLag ?? 0,
      preroll: options.preroll ?? PREROLL_SECONDS,
      tail: options.tail ?? TAIL_SECONDS,
      onProgress: options.onProgress,
    }
  }

  /**
   * Прервать и выбросить запись. Сам rAF-цикл не гасим: он должен увидеть флаг
   * и корректно разрешить свой промис, иначе run() зависнет.
   */
  cancel(): void {
    this.cancelled = true
    const recorder = this.recorder
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    this.options.video.pause()
  }

  /** Остановить раньше автостопа, но сохранить записанное. */
  finish(): void {
    this.finished = true
  }

  async run(): Promise<RecordedTake> {
    const { video, stream, lineStart, lineEnd, preroll, tail, onProgress } = this.options
    const choice = pickMimeType()
    if (!choice) throw new Error('Браузер не поддерживает запись звука (MediaRecorder)')

    const seekTarget = Math.max(0, lineStart - preroll)
    onProgress?.({ phase: 'seeking', countdown: preroll, videoTime: video.currentTime })
    video.pause()
    await waitForSeek(video, seekTarget)
    if (this.cancelled) throw new RecordingCancelled()

    const recorder =
      choice.mimeType === ''
        ? new MediaRecorder(stream)
        : new MediaRecorder(stream, { mimeType: choice.mimeType, audioBitsPerSecond: 128_000 })
    this.recorder = recorder

    const chunks: Blob[] = []
    recorder.ondataavailable = (e: BlobEvent): void => {
      if (e.data.size > 0) chunks.push(e.data)
    }

    let startedAtVideoTime = seekTarget
    let wallStart = 0
    let wallStop = 0

    const started = new Promise<void>((resolve) => {
      recorder.onstart = (): void => {
        // Пункт 4 из docs/RECORDING.md — суть синхронизации.
        startedAtVideoTime = video.currentTime
        wallStart = performance.now()
        resolve()
      }
    })
    const stopped = new Promise<void>((resolve, reject) => {
      recorder.onstop = (): void => {
        wallStop = performance.now()
        resolve()
      }
      recorder.onerror = (): void => reject(new Error('Сбой записи с микрофона'))
    })

    // Порядок важен: play() сначала, чтобы к моменту onstart видео уже шло.
    const playPromise = video.play()
    recorder.start()
    try {
      await playPromise
    } catch {
      if (recorder.state !== 'inactive') recorder.stop()
      this.detach(recorder)
      throw new Error('Браузер не дал запустить видео. Нажмите на плеер и повторите.')
    }
    await started
    if (this.cancelled) {
      if (recorder.state !== 'inactive') recorder.stop()
      this.detach(recorder)
      throw new RecordingCancelled()
    }

    const stopAt = lineEnd + tail
    await new Promise<void>((resolve) => {
      const loop = (): void => {
        if (this.cancelled || this.finished) {
          resolve()
          return
        }
        const now = video.currentTime
        const phase: RecordingPhase = now < lineStart ? 'preroll' : 'recording'
        onProgress?.({ phase, countdown: Math.max(0, lineStart - now), videoTime: now })
        if (now >= stopAt || video.ended) {
          this.finished = true
          resolve()
          return
        }
        this.rafId = window.requestAnimationFrame(loop)
      }
      this.rafId = window.requestAnimationFrame(loop)
    })
    this.stopLoop()

    onProgress?.({ phase: 'finishing', countdown: 0, videoTime: video.currentTime })
    video.pause()
    if (recorder.state !== 'inactive') recorder.stop()
    await stopped
    this.detach(recorder)
    if (this.cancelled) throw new RecordingCancelled()

    const mimeType = recorder.mimeType || choice.mimeType || 'audio/webm'
    const blob = new Blob(chunks, { type: mimeType })
    if (blob.size === 0) throw new Error('Записать не удалось: пустой файл. Проверьте микрофон.')

    const decoded = await decodeDuration(blob)
    const duration = decoded ?? Math.max(0, (wallStop - wallStart) / 1000)

    // Содержимое файла начинается на таймкоде startedAtVideoTime + lag.
    // Сервер ставит дубль на line.start + offset, отсюда offset.
    const offset = startedAtVideoTime + this.options.recorderLag - lineStart

    return {
      blob,
      mimeType,
      filename: `take.${extensionFor(mimeType)}`,
      duration,
      offset,
      startedAtVideoTime,
    }
  }

  private stopLoop(): void {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  private detach(recorder: MediaRecorder): void {
    recorder.ondataavailable = null
    recorder.onstart = null
    recorder.onstop = null
    recorder.onerror = null
    this.recorder = null
  }
}
